import React, { useMemo } from 'react';
import { Award, TrendingUp, TrendingDown, Trophy } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { useTranslation } from '../../i18n';

/**
 * 02/06/2026 — Cumulative profit & achievements widget.
 *
 * Surfaced on the Dashboard to give the player a tangible "score" of how
 * well the company has been run. Sums `netResult` across all months in
 * `monthlyHistory` and shows the count of unlocked achievements.
 *
 * Player feedback (02/06/2026) : "Les bénéfices ne sont affichés nulle part."
 * → this widget is the answer.
 */
export default function BenefitsCard() {
  const { t } = useTranslation();
  const monthlyHistory = useGameStore(s => s.monthlyHistory || EMPTY_ARRAY);
  const yearlyReports = useGameStore(s => s.yearlyReports || EMPTY_ARRAY);
  const achievements = useGameStore(s => s.achievements || EMPTY_ARRAY);

  const { cumulNet, last12Net, bestMonth, monthsRun } = useMemo(() => {
    const months = monthlyHistory.slice();
    const cumul = months.reduce((s, m) => s + (m.netResult || 0), 0);
    const last12 = months.slice(-12).reduce((s, m) => s + (m.netResult || 0), 0);
    const best = months.reduce(
      (acc, m) => (m.netResult || -Infinity) > (acc?.netResult ?? -Infinity) ? m : acc,
      null,
    );
    return {
      cumulNet: Math.round(cumul),
      last12Net: Math.round(last12),
      bestMonth: best,
      monthsRun: months.length,
    };
  }, [monthlyHistory]);

  const trendUp = last12Net >= 0;
  const positiveYears = yearlyReports.filter(y => (y.netResult || 0) > 0).length;
  const achievementsCount = achievements.length;

  const fmt = (n) => `${n >= 0 ? '+' : ''}${n.toLocaleString('fr-FR')} €`;

  if (monthsRun === 0) return null; // hide on day 0

  return (
    <div className="surface-card overflow-hidden" data-testid="benefits-card">
      <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-300" />
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            {t('benefits_card_title')}
          </h3>
        </div>
        <span className="text-[10px] text-slate-500 font-mono">{t('benefits_card_months_run', { n: monthsRun })}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
        <StatCell
          icon={Award}
          label={t('benefits_card_cumul')}
          value={fmt(cumulNet)}
          tone={cumulNet >= 0 ? 'money' : 'danger'}
          testid="cumul-net"
        />
        <StatCell
          icon={trendUp ? TrendingUp : TrendingDown}
          label={t('benefits_card_last12')}
          value={fmt(last12Net)}
          tone={trendUp ? 'money' : 'danger'}
          testid="last12-net"
        />
        <StatCell
          icon={Trophy}
          label={t('benefits_card_best_month')}
          value={bestMonth ? fmt(Math.round(bestMonth.netResult || 0)) : '—'}
          sub={bestMonth ? `${bestMonth.month}/${bestMonth.year}` : null}
          tone="warn"
          testid="best-month"
        />
        <StatCell
          icon={Award}
          label={t('benefits_card_rewards')}
          value={`${achievementsCount}${
            positiveYears > 0
              ? ` · ${t(positiveYears > 1 ? 'benefits_card_rewards_positive_years_many' : 'benefits_card_rewards_positive_years_one', { n: positiveYears })}`
              : ''
          }`}
          sub={achievementsCount > 0 ? t('benefits_card_rewards_sub_some') : t('benefits_card_rewards_sub_none')}
          tone="accent"
          testid="rewards-count"
        />
      </div>
    </div>
  );
}

function StatCell({ icon: Icon, label, value, sub, tone, testid }) {
  const toneClass = {
    money: 'text-money',
    danger: 'text-danger',
    warn: 'text-amber-300',
    accent: 'text-accent',
  }[tone] || 'text-foreground';
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
        <Icon className={`w-3 h-3 ${toneClass}`} />
        <span>{label}</span>
      </div>
      <div className={`text-base font-mono font-bold ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
